import { useAdmin } from './useAdmin';
import { useNetwork, useSwitchNetwork } from 'wagmi';

export function useNetworkRestrictions() {
  const { isAdmin } = useAdmin();
  const { chain } = useNetwork();
  const { chains: availableChains } = useSwitchNetwork();

  const BSC_MAINNET_CHAIN_ID = 56;
  const BSC_TESTNET_CHAIN_ID = 97;
  const ETH_MAINNET_CHAIN_ID = 1;
  const SEPOLIA_CHAIN_ID = 11155111;
  const AVALANCHE_MAINNET_CHAIN_ID = 43114;
  const AVALANCHE_FUJI_CHAIN_ID = 43113;
  const ABSTRACT_MAINNET_CHAIN_ID = 2741;
  const ABSTRACT_TESTNET_CHAIN_ID = 11124;
  const BASE_MAINNET_CHAIN_ID = 8453;
  const BASE_SEPOLIA_CHAIN_ID = 84532;
  const OP_MAINNET_CHAIN_ID = 10;
  const OP_SEPOLIA_CHAIN_ID = 11155420;
  const ARBITRUM_MAINNET_CHAIN_ID = 42161;
  const ARBITRUM_SEPOLIA_CHAIN_ID = 421614;
  const POLYGON_MAINNET_CHAIN_ID = 137;
  const POLYGON_AMOY_CHAIN_ID = 80002;
  const SEI_MAINNET_CHAIN_ID = 1329;
  const SEI_TESTNET_CHAIN_ID = 1328;
  const OPBNB_MAINNET_CHAIN_ID = 204;
  const OPBNB_TESTNET_CHAIN_ID = 5611;

  const isBscTestnet = chain?.id === BSC_TESTNET_CHAIN_ID;
  const isBscMainnet = chain?.id === BSC_MAINNET_CHAIN_ID;
  const isEthMainnet = chain?.id === ETH_MAINNET_CHAIN_ID;
  const isSepolia = chain?.id === SEPOLIA_CHAIN_ID;
  const isAvalancheMainnet = chain?.id === AVALANCHE_MAINNET_CHAIN_ID;
  const isAvalancheFuji = chain?.id === AVALANCHE_FUJI_CHAIN_ID;
  const isAbstractMainnet = chain?.id === ABSTRACT_MAINNET_CHAIN_ID;
  const isAbstractTestnet = chain?.id === ABSTRACT_TESTNET_CHAIN_ID;
  const isBaseMainnet = chain?.id === BASE_MAINNET_CHAIN_ID;
  const isBaseSepolia = chain?.id === BASE_SEPOLIA_CHAIN_ID;
  const isOpMainnet = chain?.id === OP_MAINNET_CHAIN_ID;
  const isOpSepolia = chain?.id === OP_SEPOLIA_CHAIN_ID;
  const isArbitrumMainnet = chain?.id === ARBITRUM_MAINNET_CHAIN_ID;
  const isArbitrumSepolia = chain?.id === ARBITRUM_SEPOLIA_CHAIN_ID;
  const isPolygonMainnet = chain?.id === POLYGON_MAINNET_CHAIN_ID;
  const isPolygonAmoy = chain?.id === POLYGON_AMOY_CHAIN_ID;
  const isSeiMainnet = chain?.id === SEI_MAINNET_CHAIN_ID;
  const isSeiTestnet = chain?.id === SEI_TESTNET_CHAIN_ID;
  const isOpbnbMainnet = chain?.id === OPBNB_MAINNET_CHAIN_ID;
  const isOpbnbTestnet = chain?.id === OPBNB_TESTNET_CHAIN_ID;

  // Network is supported if:
  // 1. User is on BSC mainnet, Avalanche mainnet, Abstract mainnet, Arbitrum mainnet, Optimism mainnet, Base mainnet, Polygon mainnet, Sei mainnet, or Ethereum mainnet (everyone allowed)
  // 2. User is on BSC testnet, Sepolia, Avalanche Fuji, Abstract testnet, Base Sepolia, Polygon Amoy, Sei testnet AND is an admin
  const isNetworkSupported = 
    isBscMainnet || 
    isAvalancheMainnet || 
    isAbstractMainnet ||
    isArbitrumMainnet ||
    isOpMainnet ||
    isBaseMainnet ||
    isEthMainnet ||
    isPolygonMainnet ||
    isSeiMainnet ||
    isOpbnbMainnet ||
    ((isBscTestnet || isSepolia || isAvalancheFuji || isAbstractTestnet || isBaseSepolia || isOpSepolia || isArbitrumSepolia || isPolygonAmoy || isSeiTestnet || isOpbnbTestnet) && isAdmin);

  // Debug logging to help diagnose network support issues
  if (chain) {
    console.log(`Network check: Chain ID ${chain.id}, Admin: ${isAdmin}, Supported: ${isNetworkSupported}`);
    
    // Log specific network flags for debugging
    if (isAbstractMainnet || isAbstractTestnet) {
      console.log(`On Abstract network: Mainnet=${isAbstractMainnet}, Testnet=${isAbstractTestnet}`);
    }
  }

  // Get the allowed networks based on admin status and available chains
  const getAllowedNetworks = () => {
    const networks = [];
    
    // Always include BSC, Avalanche, Abstract, Arbitrum, Optimism, Base, Polygon, Sei, and Ethereum mainnets
    if (availableChains.some(c => c.id === BSC_MAINNET_CHAIN_ID)) {
      networks.push({ name: "BNB Smart Chain", chainId: BSC_MAINNET_CHAIN_ID });
    }
    if (availableChains.some(c => c.id === AVALANCHE_MAINNET_CHAIN_ID)) {
      networks.push({ name: "Avalanche", chainId: AVALANCHE_MAINNET_CHAIN_ID });
    }
    if (availableChains.some(c => c.id === ABSTRACT_MAINNET_CHAIN_ID)) {
      networks.push({ name: "Abstract", chainId: ABSTRACT_MAINNET_CHAIN_ID });
    }
    if (availableChains.some(c => c.id === ARBITRUM_MAINNET_CHAIN_ID)) {
      networks.push({ name: "Arbitrum One", chainId: ARBITRUM_MAINNET_CHAIN_ID });
    }
    if (availableChains.some(c => c.id === OP_MAINNET_CHAIN_ID)) {
      networks.push({ name: "Optimism", chainId: OP_MAINNET_CHAIN_ID });
    }
    if (availableChains.some(c => c.id === BASE_MAINNET_CHAIN_ID)) {
      networks.push({ name: "Base", chainId: BASE_MAINNET_CHAIN_ID });
    }
    if (availableChains.some(c => c.id === POLYGON_MAINNET_CHAIN_ID)) {
      networks.push({ name: "Polygon", chainId: POLYGON_MAINNET_CHAIN_ID });
    }
    if (availableChains.some(c => c.id === SEI_MAINNET_CHAIN_ID)) {
      networks.push({ name: "Sei", chainId: SEI_MAINNET_CHAIN_ID });
    }
    if (availableChains.some(c => c.id === OPBNB_MAINNET_CHAIN_ID)) {
      networks.push({ name: "opBNB", chainId: OPBNB_MAINNET_CHAIN_ID });
    }
    if (availableChains.some(c => c.id === ETH_MAINNET_CHAIN_ID)) {
      networks.push({ name: "Ethereum", chainId: ETH_MAINNET_CHAIN_ID });
    }
    
    // Include testnets only for admins
    if (isAdmin) {
      if (availableChains.some(c => c.id === BSC_TESTNET_CHAIN_ID)) {
        networks.push({ name: "BSC Testnet", chainId: BSC_TESTNET_CHAIN_ID });
      }
      if (availableChains.some(c => c.id === SEPOLIA_CHAIN_ID)) {
        networks.push({ name: "Sepolia", chainId: SEPOLIA_CHAIN_ID });
      }
      if (availableChains.some(c => c.id === AVALANCHE_FUJI_CHAIN_ID)) {
        networks.push({ name: "Avalanche Fuji", chainId: AVALANCHE_FUJI_CHAIN_ID });
      }
      if (availableChains.some(c => c.id === ABSTRACT_TESTNET_CHAIN_ID)) {
        networks.push({ name: "Abstract Sepolia", chainId: ABSTRACT_TESTNET_CHAIN_ID });
      }
      if (availableChains.some(c => c.id === BASE_MAINNET_CHAIN_ID)) {
        networks.push({ name: "Base", chainId: BASE_MAINNET_CHAIN_ID });
      }
      if (availableChains.some(c => c.id === BASE_SEPOLIA_CHAIN_ID)) {
        networks.push({ name: "Base Sepolia", chainId: BASE_SEPOLIA_CHAIN_ID });
      }
      if (availableChains.some(c => c.id === OP_MAINNET_CHAIN_ID)) {
        networks.push({ name: "OP Mainnet", chainId: OP_MAINNET_CHAIN_ID });
      }
      if (availableChains.some(c => c.id === OP_SEPOLIA_CHAIN_ID)) {
        networks.push({ name: "OP Sepolia", chainId: OP_SEPOLIA_CHAIN_ID });
      }
      if (availableChains.some(c => c.id === ARBITRUM_SEPOLIA_CHAIN_ID)) {
        networks.push({ name: "Arbitrum Sepolia", chainId: ARBITRUM_SEPOLIA_CHAIN_ID });
      }
      if (availableChains.some(c => c.id === POLYGON_AMOY_CHAIN_ID)) {
        networks.push({ name: "Polygon Amoy", chainId: POLYGON_AMOY_CHAIN_ID });
      }
      if (availableChains.some(c => c.id === SEI_TESTNET_CHAIN_ID)) {
        networks.push({ name: "Sei Atlantic", chainId: SEI_TESTNET_CHAIN_ID });
      }
      if (availableChains.some(c => c.id === OPBNB_TESTNET_CHAIN_ID)) {
        networks.push({ name: "opBNB Testnet", chainId: OPBNB_TESTNET_CHAIN_ID });
      }
    }
    
    return networks;
  };

  return {
    isNetworkSupported,
    isBscTestnet,
    isBscMainnet,
    isEthMainnet,
    isSepolia,
    isAvalancheMainnet,
    isAvalancheFuji,
    isAbstractMainnet,
    isAbstractTestnet,
    getAllowedNetworks,
    BSC_MAINNET_CHAIN_ID,
    BSC_TESTNET_CHAIN_ID,
    ETH_MAINNET_CHAIN_ID,
    SEPOLIA_CHAIN_ID,
    AVALANCHE_MAINNET_CHAIN_ID,
    AVALANCHE_FUJI_CHAIN_ID,
    ABSTRACT_MAINNET_CHAIN_ID,
    ABSTRACT_TESTNET_CHAIN_ID,
    isBaseMainnet,
    isBaseSepolia,
    BASE_MAINNET_CHAIN_ID,
    BASE_SEPOLIA_CHAIN_ID,
    isOpMainnet,
    isOpSepolia,
    OP_MAINNET_CHAIN_ID,
    OP_SEPOLIA_CHAIN_ID,
    isArbitrumMainnet,
    isArbitrumSepolia,
    ARBITRUM_MAINNET_CHAIN_ID,
    ARBITRUM_SEPOLIA_CHAIN_ID,
    isPolygonMainnet,
    isPolygonAmoy,
    POLYGON_MAINNET_CHAIN_ID,
    POLYGON_AMOY_CHAIN_ID,
    isSeiMainnet,
    isSeiTestnet,
    SEI_MAINNET_CHAIN_ID,
    SEI_TESTNET_CHAIN_ID,
    isOpbnbMainnet,
    isOpbnbTestnet,
    OPBNB_MAINNET_CHAIN_ID,
    OPBNB_TESTNET_CHAIN_ID,
  };
}
